<template>
  <div class="messenger-chat-block">
    <div class="d-flex align-center">
      <div class="flex-shrink-0 flex-grow-0 mr-3">
        <widget-skeleton
          v-if="isLoading"
          type="circle"
          width="40px"
          height="40px"
        />
        <div
          v-else
          v-bind="getThemeIconProps('chat-avatar')"
          class="chat-avatar"
          :class="{
            'chat-avatar--online': !!avatarUrl,
          }"
        >
          <img
            v-if="avatarUrl"
            :src="avatarUrl"
          />
          <widget-icon
            v-else
            name="tmi-message-bubble"
            size="20px"
          />
        </div>
      </div>

      <div class="flex-shrink-1 flex-grow-1 min-w0">
        <div class="d-flex align-center justify-space-between mb-2px">
          <div class="flex-shrink-1 flex-grow-1 min-w0">
            <div class="messenger-chat-block__title">
              <widget-skeleton
                v-if="isLoading"
                class="inline-block"
                type="QChip"
                width="120px"
                height="15px"
              />
              <span v-else>{{ title }}</span>
            </div>
          </div>
          <div class="flex-shrink-0 flex-grow-0 ml-4">
            <div class="messenger-chat-block__date">
              <widget-skeleton
                v-if="isLoading"
                class="inline-block"
                type="QChip"
                width="50px"
                height="12px"
              />
              <span v-else>{{ lastMessageDate }}</span>
            </div>
          </div>
        </div>

        <div class="d-flex align-center justify-space-between">
          <div class="flex-shrink-1 flex-grow-1 min-w0">
            <div class="messenger-chat-block__last-message">
              <widget-skeleton
                v-if="isLoading"
                class="inline-block"
                type="QChip"
                width="150px"
                height="10px"
              />
              <messenger-text-with-emoji
                v-else
                :text="lastMessageText"
                emoji-size="14px"
              />
            </div>
          </div>
          <div class="flex-shrink-0 flex-grow-0 ml-4">
            <widget-skeleton
              v-if="isLoading"
              class="inline-block"
              type="QChip"
              width="22px"
              height="16px"
            />
            <div
              v-else-if="numberOfNewMessages"
              class="messenger-chat-block__new-messages-count"
            >
              {{ numberOfNewMessages }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="messenger-chat-block__divider" />

    <div class="text-center">
      <widget-link
        variant="primary"
        size="16"
        @click="$emit('back-to-chat')"
      >
        <div class="d-flex align-center">
          <span>{{ $t('messengerWidget.backToChat') }}</span>
          <widget-icon
            class="ml-1"
            name="tmi-chevron-right"
            size="18px"
          />
        </div>
      </widget-link>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isChatLoadingState, isChatOpenedState, type MessengerChatState } from '@widgets/messenger/types/chat/chat'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import translateService from '@widgets/shared/services/translate/translateService'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import {
  isIncomingMessageListItem,
  isInitialMessageListItem,
  isSentMessageListItem,
  type MessengerLastMessage,
} from '@widgets/messenger/types/chat/message'
import DateTimeService, {
  DATE_FORMAT,
  dateTimeService,
  type DurationFormattingParams,
} from '@widgets/shared/services/dateTimeService'
import WidgetSkeleton from '@widgets/shared/components/WidgetSkeleton.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import WidgetLink from '@widgets/shared/components/WidgetLink.vue'
import { computed } from '@/composition/vue/compositionApi'
import { useComputedFromTime } from '@/composition/useComputedFromTime'

const { chatState, numberOfNewMessages, lastMessage } = defineProps<{
  chatState: MessengerChatState
  numberOfNewMessages?: number
  lastMessage?: MessengerLastMessage
}>()

defineEmits<{
  (e: 'back-to-chat'): void
}>()

const { getThemeIconProps } = useWidgetTheme()

const isLoading = computed(() => isChatLoadingState(chatState))

const title = computed(() => {
  return isChatOpenedState(chatState) ? chatState.agent.name : translateService.t('chat.liveChat')
})

const avatarUrl = computed(() => {
  if (isChatOpenedState(chatState)) {
    return chatState.agent.avatarUrl
  }
  return ''
})

const lastMessageText = computed(() => {
  if (!lastMessage) {
    return ''
  }

  let prefix = ''
  if (isSentMessageListItem(lastMessage) || isInitialMessageListItem(lastMessage)) {
    prefix = translateService.t('common.you')
  } else if (isIncomingMessageListItem(lastMessage)) {
    const assigneeFullName = isChatOpenedState(chatState) ? chatState.agent.name : ''
    const messageUserFullName = [lastMessage.firstName, lastMessage.lastName].filter(Boolean).join(' ')
    if (assigneeFullName !== messageUserFullName && lastMessage.lastName) {
      // first name + first letter of last name
      prefix = `${lastMessage.firstName} ${lastMessage.lastName[0]}.`
    }
  }

  let text = lastMessage.text
  if (!text && (isIncomingMessageListItem(lastMessage) || isSentMessageListItem(lastMessage))) {
    const attachmentCount = lastMessage.attachments.length
    if (attachmentCount === 1) {
      text = lastMessage.attachments[0].name
    } else if (attachmentCount > 1) {
      text = translateService.t('messengerWidget.nAttachments', { count: attachmentCount })
    }
  }

  return prefix ? `${prefix}: ${text}` : text
})

const lastMessageDate = useComputedFromTime(() => {
  if (!lastMessage) {
    return ''
  }
  const date = dateTimeService.toDate(lastMessage.messageTime)
  if (dateTimeService.isLessOneDay(date)) {
    if (dateTimeService.isLessOneMinute(date)) {
      return translateService.t('common.justNow')
    }
    const params: DurationFormattingParams = {
      addSuffix: true,
      shortFormatDistance: true,
    }
    const fromNow = dateTimeService.isLessOneHour(date)
      ? dateTimeService.formatDurationInHoursAndMinutes(date, dateTimeService.now(), params)
      : dateTimeService.formatDurationInHours(date, dateTimeService.now(), params)
    if (fromNow) {
      return fromNow
    }
  }
  return DateTimeService.format(date, DATE_FORMAT.DATE)
}, 60 * 1000)
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

.messenger-chat-block {
  @include widget-block();
  padding: 16px;

  &__title {
    @include truncate();
    font-size: 15px;
    line-height: 20px;
    font-weight: $bold-font;
    color: $emphasize;
  }

  &__date {
    font-size: 12px;
    line-height: 16px;
    color: $distinct;
  }

  &__last-message {
    @include truncate();
    font-size: 13px;
    line-height: 20px;
    color: $emphasize;
  }

  &__new-messages-count {
    font-size: 12px;
    line-height: 16px;
    font-weight: $semi-bold-font;
    color: $white;
    padding: 0 8px;
    border-radius: 999px;
    background-color: $red;
  }

  &__divider {
    height: 1px;
    background: $gray1;
    margin-top: 12px;
    margin-bottom: 12px;
  }
}

.chat-avatar {
  @include widget-color-variants();
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background-color: $shade;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 999px;
  }

  &--online {
    &::after {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      border-radius: 999px;
      border: 2px solid $white;
      background-color: $green;
      bottom: -4px;
      right: -4px;
    }
  }
}
</style>
